/**
 * @file useSseToast.js
 * @feature Notifications
 * @description Listens to the global `sseTaskUpdate` CustomEvent (bridged from
 * useNotificationStream) and fires a sonner toast that matches the
 * payload's status field.
 *
 * Status → toast type mapping:
 *   COMPLETED / SUCCESS / DONE / FINISHED  → toast.success  (green)
 *   FAILED    / ERROR   / CANCELLED        → toast.error    (red)
 *   RUNNING   / PENDING / IN_PROGRESS
 *     / PROCESSING / STARTED / anything else → toast.info (blue)
 *
 * Usage — call once near the top of your component tree:
 *   import { useSseToast } from './pages/auth/useSseToast';
 *
 *   // inside Pages.jsx (or any always-mounted component):
 *   useSseToast();
 *
 * Prerequisites:
 *   - <Toaster /> from sonner must be rendered somewhere in the tree
 *     (AppProviders or App.jsx):
 *       import { Toaster } from 'sonner';
 *       <Toaster position="top-right" richColors />
 *   - sonner must be installed: npm i sonner
 */

import { useEffect } from 'react';
import { toast } from 'sonner';

/* ── Status buckets (all compared case-insensitively) ──────────────────── */
const SUCCESS_STATUSES = new Set(['completed', 'success', 'done', 'succeeded', 'finish', 'finished']);
const ERROR_STATUSES   = new Set(['failed', 'failure', 'error', 'cancelled', 'canceled', 'aborted', 'rejected']);
// Everything else → info  (running, pending, in_progress, processing, started, queued, …)

/** Normalise a raw status string to one of 'success' | 'error' | 'info' */
const resolveToastType = (rawStatus) => {
  if (!rawStatus) return 'info';
  const s = String(rawStatus).toLowerCase().replace(/[-\s]/g, '_');
  if (SUCCESS_STATUSES.has(s)) return 'success';
  if (ERROR_STATUSES.has(s))   return 'error';
  return 'info';
};

/** Build a human-readable message from the SSE payload */
const buildMessage = (payload) => {
  if (payload.message)     return payload.message;
  if (payload.description) return payload.description;

  const title  = payload.title || payload.taskName || payload.task_name || payload.name || 'Task';
  const status = payload.status || '';
  return status ? `${title}: ${status}` : title;
};

/* ── Sonner options per type ───────────────────────────────────────────── */
const TOAST_OPTIONS = {
  success: { duration: 4000 },
  error:   { duration: 6000 },
  info:    { duration: 3500 },
};

/* ════════════════════════════════════════════════════════════════════════ */

export const useSseToast = () => {
  useEffect(() => {
    const handleSseEvent = (event) => {
      const payload = event?.detail;
      if (!payload) return;

      // Skip pure heartbeat / connection-confirmation frames
      if (payload.status === 'connected' || payload.type === 'heartbeat') return;

      const toastType = resolveToastType(payload.status);
      const message   = buildMessage(payload);
      const options   = TOAST_OPTIONS[toastType];

      toast[toastType](message, options);
    };

    window.addEventListener('sseTaskUpdate', handleSseEvent);
    return () => window.removeEventListener('sseTaskUpdate', handleSseEvent);
  }, []);
};